import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Inject,
  input,
  OnChanges,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {DatePipe} from '@angular/common';

import {DATE_FORMAT, EXAM_DEFAULT_FILTER, TIME_FORMAT} from '../../../api/constants';
import {Exam, ExamFilter} from '../../../api/models/exam';
import {Survey} from '../../../api/models/survey';
import {User} from '../../../api/models/user';

import {ExamService} from '../../../api/services/exam.service';
import {SurveyService} from '../../../api/services/survey.service';

import {
  ConfirmationDialogComponent
} from '../../general-purpose/dialogs/confirmation-dialog/confirmation-dialog.component';
import {ExamFilterComponent} from './exam-filter/exam-filter.component';
import {ExamListElementComponent} from './exam-list-element/exam-list-element.component';
import {
  WaButtonDirective,
  WaCalloutDirective,
  WaDividerDirective,
  WaIconDirective,
  WaInputDirective,
  WaScrollerDirective,
  WaSpinnerDirective,
  WaToastService
} from "angular-awesome";
import {Dialog} from "@angular/cdk/dialog";
import {rxResource, toSignal} from "@angular/core/rxjs-interop";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {debounceTime, lastValueFrom} from "rxjs";
import {ExamTypeService} from "../../../api/services/exam-type.service";
import {
  ExamAssignmentsSupervisionDialogComponent
} from "../exam-assignments-supervision-dialog/exam-assignments-supervision-dialog.component";
import {StaffMember} from "../../../api/models/staff-member";
import {ExamCorrectionAssignmentsData, ExamSupervisionAssignmentsData} from "../../../api/models/exam-assignments";
import {
  ExamAssignmentsCorrectionDialogComponent
} from "../exam-assignments-correction-dialog/exam-assignments-correction-dialog.component";

// const LIMIT removed (was unused in this component)

@Component({
  selector: 'app-exam-list',
  templateUrl: './exam-list.component.html',
  styleUrls: ['./exam-list.component.scss'],
  imports: [WaButtonDirective, WaIconDirective, WaScrollerDirective, WaCalloutDirective, ReactiveFormsModule, WaInputDirective, WaDividerDirective, WaSpinnerDirective, ExamListElementComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ExamListComponent implements OnChanges {

  readonly user = input<User | null>(null);
  readonly survey = input.required<Survey>();

  defaultFilter = EXAM_DEFAULT_FILTER;

  filter: ExamFilter = this.defaultFilter;
  moduleCodeFilter = new FormControl();
  moduleNameFilter = new FormControl();

  moduleCode = toSignal(this.moduleCodeFilter.valueChanges.pipe(debounceTime(500)));
  moduleName = toSignal(this.moduleNameFilter.valueChanges.pipe(debounceTime(500)));

  exams_params = computed(() => ({
    survey_id: this.survey().id, skip: 0, limit: 100, filter: {
      ...this.filter,
      moduleCode: this.moduleCode()?.length >= 2 ? this.moduleCode() : null,
      moduleName: this.moduleName()?.length >= 3 ? this.moduleName() : null
    }
  }));
  exams = rxResource({
    params: this.exams_params,
    stream: ({params}) => this.surveyService.getExams(params.survey_id, params.skip, params.limit, params.filter)
  });

  // ExamsView as a readonly view sorted by module number
  examsView = computed<Exam[]>(() => this.exams.hasValue() ? this.exams.value().data.toSorted((a, b) => {
    const moduleCodeCompare = a.module.code.localeCompare(b.module.code);
    return moduleCodeCompare;
  }) : []);

  //selectedExam: Exam | null = null;
  totalCount: number = 0;
  @Output() selectedEvent = new EventEmitter<Exam | null>();
  private isLoading = false;
  private staffMembers: StaffMember[] = [];
  private lockAcquired: boolean = false;

  constructor(private dialog: Dialog, private datePipe: DatePipe, private examService: ExamService, private examTypeService: ExamTypeService, private surveyService: SurveyService, private toastService: WaToastService, @Inject(DATE_FORMAT) public dateFormat: string, @Inject(TIME_FORMAT) public timeFormat: string) {
  }


  async editSupervisions(exam: Exam): Promise<void> {
    // load data
    this.isLoading = true;
    // lock exam (treat success if request resolves)
    try {
      await lastValueFrom(this.examService.lock(exam.id));
      this.lockAcquired = true;
    } catch (error: any) {
      this.isLoading = false;
      this.toastService.danger("No lock could be acquired, please try again.");
      return;
    }
    // get staff members (load once and cache)
    if (this.staffMembers.length === 0) {
      try {
        const result = await lastValueFrom(this.surveyService.getStaffMembers(this.survey().id));
        // result is a StaffMemberSearchResult { count, data }
        this.staffMembers = result.data.filter(staffMember => this.hasValidContract(staffMember));
      } catch (error: any) {
        this.toastService.danger(`Error obtaining staff members for survey ${this.survey().id}`);
      }
    }
    // load supervising / exempt / suggested lists (must finish before opening dialog)
    const surveyId = this.survey().id;
    const examId = exam.id;

    // load supervising / exempt / suggested lists (must finish before opening dialog)
    let fixedSupervisors: StaffMember[] = [];
    let excludedSupervisors: StaffMember[] = [];
    let suggestedSupervisors: StaffMember[] = [];

    try {
      const supervising = await this.surveyService.getSupervising(surveyId, examId);
      fixedSupervisors = supervising.data.map((item: any) => item.staff_member);

      const exempt = await this.surveyService.getExemptFromSupervising(surveyId, examId);
      excludedSupervisors = exempt.data.map((item: any) => item.staff_member);

      const suggested = await this.surveyService.getSuggestedSupervising(surveyId, examId);
      suggestedSupervisors = suggested.data.map((item: any) => item.staff_member);
      } catch (error: any) {
        // abort: show toast, release lock and stop
        this.toastService.danger(`Error obtaining supervisor lists for exam ${exam.id}. Aborting.`);
        if (this.lockAcquired) {
          this.examService.unlock(exam.id).subscribe({ next: () => this.lockAcquired = false });
        }
        this.isLoading = false;
        return;
    }

    //store these before they are modified in the dialog
    const oldFixedIDs = fixedSupervisors.map(s => s.id);
    const oldExcludedIDs = excludedSupervisors.map(s => s.id);

    const ref = this.dialog.open<ExamSupervisionAssignmentsData>(ExamAssignmentsSupervisionDialogComponent, {
      disableClose: true,
      data: {
        exam: exam,
        survey: this.survey(),
        staffMembers: this.staffMembers,
        fixedSupervisors: fixedSupervisors,
        suggestedSupervisors: suggestedSupervisors,
        excludedSupervisors: excludedSupervisors
      },
    });

    ref.closed.subscribe(async supervisionAssignmentData => {
      const errors: string[] = [];
      // If dialog returned data, apply changes
      if (supervisionAssignmentData) {
        const newFixedIDs = supervisionAssignmentData.fixedSupervisors.map((s: any) => s.id);
        const newExcludedIDs = supervisionAssignmentData.excludedSupervisors.map((s: any) => s.id);

        // Build id->name map from available lists (preference: dialog-returned lists then cached staffMembers)
        const idNameMap: Record<number, string> = {};
        const addToMap = (arr: any[]) => arr?.forEach(it => { if (it && it.id) idNameMap[it.id] = `${it.first_name} ${it.last_name}`; });
        addToMap(this.staffMembers);
        addToMap(fixedSupervisors);
        addToMap(excludedSupervisors);
        addToMap(supervisionAssignmentData.fixedSupervisors);
        addToMap(supervisionAssignmentData.excludedSupervisors);

        try {
          const fixedErrors = await this.updateFixedSupervising(exam, newFixedIDs, oldFixedIDs, idNameMap);
          errors.push(...fixedErrors);

          const excludedErrors = await this.updateExcludedSupervising(exam, newExcludedIDs, oldExcludedIDs, idNameMap);
          errors.push(...excludedErrors);
        } catch (err: any) {
          // fallback
          errors.push(err?.message ?? String(err));
        }
      }

      if (errors.length > 0) {
        const combined = errors.join('\n');
        this.toastService.danger(`Errors occurred while updating supervisors for exam ${exam.id}:\n${combined}`);
      }

      // release lock
      if (this.lockAcquired) {
        this.examService.unlock(exam.id).subscribe({
          next: () => this.lockAcquired = false,
          error: _err => {
            this.toastService.danger(`Failed to release lock on exam ${exam.id}.`)
          }
        });
      }
      this.isLoading = false;
    });
  }

  async updateFixedSupervising(exam: Exam, newFixedIDs: number[], oldFixedIDs: number[], idNameMap?: Record<number,string>): Promise<string[]> {
    const errors: string[] = [];
    const survey = this.survey();
    if (!survey) return ['Survey not available'];
    const surveyId = survey.id;
    const examId = exam.id;

    const toAdd = newFixedIDs.filter(s => !oldFixedIDs.includes(s));
    const toRemove = oldFixedIDs.filter(s => !newFixedIDs.includes(s));

    // Add new supervisors
    for (const staffId of toAdd) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.addSupervising(surveyId, examId, staffId, null);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not add ${name} as a supervisor for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    // Remove supervisors
    for (const staffId of toRemove) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.removeSupervising(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not remove ${name} from supervisors for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    return errors;
  }


  async updateExcludedSupervising(exam: Exam, newExcludedIDs: number[], oldExcludedIDs: number[], idNameMap?: Record<number,string>): Promise<string[]> {
    const errors: string[] = [];
    const survey = this.survey();
    if (!survey) return ['Survey not available'];
    const surveyId = survey.id;
    const examId = exam.id;

    const toAdd = newExcludedIDs.filter(s => !oldExcludedIDs.includes(s));
    const toRemove = oldExcludedIDs.filter(s => !newExcludedIDs.includes(s));

    // To exclude: call exemptFromSupervising; to re-include: call undoExemptFromSupervising
    for (const staffId of toAdd) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.exemptFromSupervising(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not exclude ${name} from supervising for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    for (const staffId of toRemove) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.undoExemptFromSupervising(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not remove exclusion from supervision for ${name} for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    return errors;
  }

  async editCorrections(exam: Exam): Promise<void> {
    // load data
    this.isLoading = true;
    // lock exam (treat success if request resolves)
    try {
      await lastValueFrom(this.examService.lock(exam.id));
      this.lockAcquired = true;
    } catch (error: any) {
      this.isLoading = false;
      this.toastService.danger("No lock could be acquired, please try again.");
      return;
    }
    // get staff members (load once and cache)
    if (this.staffMembers.length === 0) {
      try {
        const result = await lastValueFrom(this.surveyService.getStaffMembers(this.survey().id));
        // result is a StaffMemberSearchResult { count, data }
        this.staffMembers = result.data.filter(staffMember => this.hasValidContract(staffMember));
      } catch (error: any) {
        this.toastService.danger(`Error obtaining staff members for survey ${this.survey().id}`);
      }
    }
    // load supervising / exempt / suggested lists (must finish before opening dialog)
    const surveyId = this.survey().id;
    const examId = exam.id;

    // load correcting / exempt / suggested lists (must finish before opening dialog)
    let fixedCorrectors: StaffMember[] = [];
    let excludedCorrectors: StaffMember[] = [];
    let suggestedCorrectors: StaffMember[] = [];

    try {
      const correcting = await this.surveyService.getCorrecting(surveyId, examId);
      fixedCorrectors = correcting.data.map((item: any) => item.staff_member);

      const exempt = await this.surveyService.getExemptFromCorrecting(surveyId, examId);
      excludedCorrectors = exempt.data.map((item: any) => item.staff_member);

      const suggested = await this.surveyService.getSuggestedCorrecting(surveyId, examId);
      suggestedCorrectors = suggested.data.map((item: any) => item.staff_member);
      } catch (error: any) {
        // abort: show toast, release lock and stop
        this.toastService.danger(`Error obtaining corrector lists for exam ${exam.id}. Aborting.`);
        if (this.lockAcquired) {
          this.examService.unlock(exam.id).subscribe({ next: () => this.lockAcquired = false });
        }
        this.isLoading = false;
        return;
    }

    //store these before they are modified in the dialog
    const oldFixedIDs = fixedCorrectors.map(s => s.id);
    const oldExcludedIDs = excludedCorrectors.map(s => s.id);

    const ref = this.dialog.open<ExamCorrectionAssignmentsData>(ExamAssignmentsCorrectionDialogComponent, {
      disableClose: true,
      data: {
        exam: exam,
        survey: this.survey(),
        staffMembers: this.staffMembers,
        fixedCorrectors: fixedCorrectors,
        suggestedCorrectors: suggestedCorrectors,
        excludedCorrectors: excludedCorrectors
      },
    });

    ref.closed.subscribe(async correctionAssignmentData => {
      const errors: string[] = [];
      // If dialog returned data, apply changes
      if (correctionAssignmentData) {
        const newFixedIDs = correctionAssignmentData.fixedCorrectors.map((s: any) => s.id);
        const newExcludedIDs = correctionAssignmentData.excludedCorrectors.map((s: any) => s.id);

        // Build id->name map from available lists (preference: dialog-returned lists then cached staffMembers)
        const idNameMap: Record<number, string> = {};
        const addToMap = (arr: any[]) => arr?.forEach(it => { if (it && it.id) idNameMap[it.id] = `${it.first_name} ${it.last_name}`; });
        addToMap(this.staffMembers);
        addToMap(fixedCorrectors);
        addToMap(excludedCorrectors);
        addToMap(correctionAssignmentData.fixedCorrectors);
        addToMap(correctionAssignmentData.excludedCorrectors);

        try {
          const fixedErrors = await this.updateFixedCorrecting(exam, newFixedIDs, oldFixedIDs, idNameMap);
          errors.push(...fixedErrors);

          const excludedErrors = await this.updateExcludedCorrecting(exam, newExcludedIDs, oldExcludedIDs, idNameMap);
          errors.push(...excludedErrors);
        } catch (err: any) {
          // fallback
          errors.push(err?.message ?? String(err));
        }
      }

      if (errors.length > 0) {
        const combined = errors.join('\n');
        this.toastService.danger(`Errors occurred while updating correctors for exam ${exam.id}:\n${combined}`);
      }

      // release lock
      if (this.lockAcquired) {
        this.examService.unlock(exam.id).subscribe({
          next: () => this.lockAcquired = false,
          error: _err => {
            this.toastService.danger(`Failed to release lock on exam ${exam.id}.`)
          }
        });
      }
      this.isLoading = false;
    });
  }

  async updateFixedCorrecting(exam: Exam, newFixedIDs: number[], oldFixedIDs: number[], idNameMap?: Record<number,string>): Promise<string[]> {
    const errors: string[] = [];
    const survey = this.survey();
    if (!survey) return ['Survey not available'];
    const surveyId = survey.id;
    const examId = exam.id;

    const toAdd = newFixedIDs.filter(s => !oldFixedIDs.includes(s));
    const toRemove = oldFixedIDs.filter(s => !newFixedIDs.includes(s));

    // Add new correctors
    for (const staffId of toAdd) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.addCorrecting(surveyId, examId, staffId, 1); //TODO: for now, always assign '1' unit, but in the future we might want to allow editing this as well
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not add ${name} as a corrector for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    // Remove correctors
    for (const staffId of toRemove) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.removeCorrecting(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not remove ${name} from correction for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    return errors;
  }


  async updateExcludedCorrecting(exam: Exam, newExcludedIDs: number[], oldExcludedIDs: number[], idNameMap?: Record<number,string>): Promise<string[]> {
    const errors: string[] = [];
    const survey = this.survey();
    if (!survey) return ['Survey not available'];
    const surveyId = survey.id;
    const examId = exam.id;

    const toAdd = newExcludedIDs.filter(s => !oldExcludedIDs.includes(s));
    const toRemove = oldExcludedIDs.filter(s => !newExcludedIDs.includes(s));

    // To exclude: call exemptFromCorrecting; to re-include: call undoExemptFromCorrecting
    for (const staffId of toAdd) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.exemptFromCorrecting(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not exclude ${name} from correction for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    for (const staffId of toRemove) {
      const name = idNameMap?.[staffId] ?? `ID ${staffId}`;
      try {
        await this.surveyService.undoExemptFromCorrecting(surveyId, examId, staffId);
      } catch (err: any) {
        const reason = err?.message ?? String(err);
        const dateStr = this.datePipe.transform(exam.main_exam.begin, this.dateFormat);
        errors.push(`Could not remove exclusion from correction for ${name} for exam ${exam.module.code} on ${dateStr}. ${reason}`);
      }
    }

    return errors;
  }

  editExam(_exam: Exam): void {
    // TODO: Future add-on
  }

  //  this.selectedExam = exam;
  //  this.selectedEvent.emit(exam);
  // }

  removeExam(exam: Exam): void {
    const examName = exam.module.code;
    const ref = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true, data: {
        title: 'Confirm deletion', message: `Are you sure that you want to delete the exam for module
                  ${exam.module.code} on ${this.datePipe.transform(exam.main_exam.begin, this.dateFormat)}?`
      }
    });

    ref.closed.subscribe(data => {
      if (!data) return;
      this.examService.delete(exam.id).subscribe({
        next: () => {
          this.exams.update((items) => items ? {
            count: items.count - 1, data: items.data.filter(elt => elt !== exam)
          } : undefined);
          this.toastService.success(`Exam ${examName} has been deleted.`);
        }, error: _err => {
          this.toastService.danger(`Could not delete exam ${examName}.`);
        }
      });
    });
  }

  showFilterDialog(): void {
    const ref = this.dialog.open<ExamFilter>(ExamFilterComponent, {
      disableClose: true, data: {filter: this.filter}
    });

    ref.closed.subscribe(filter => {
      if (!filter) return;
      this.filter = filter;
      this.exams.reload();
    });
  }

  resetFilter(): void {
    this.filter = this.defaultFilter;
    this.moduleCodeFilter.setValue('');
    this.moduleNameFilter.setValue('');
    this.exams.reload();
  }

  showCreateDialog(): void {
    // TODO: Future add-on
  }

  ngOnChanges(): void {
    const user = this.user();
    const survey = this.survey();

    if (user && survey) {
      this.exams.reload();
      return;
    }

    this.totalCount = this.exams.hasValue() ? this.exams.value().count : 0;
    //this.selectedExam = null;
  }

  //ngOnInit(): void {
  //  this.getExams();
  // }

  // Basic validity check for contracts:
  // Returns true if at least one contract's date-only interval overlaps
  // with the survey's date-only interval (i.e. they share at least one day).
  private hasValidContract(staffMember: StaffMember): boolean {
    if (!Array.isArray(staffMember.contracts) || staffMember.contracts.length === 0) return false;
    const survey = this.survey();
    if (!survey) return false;

    const toDateOnly = (d: any): Date => {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x;
    };

    const surveyBegin = toDateOnly(survey.begin);
    const surveyEnd = toDateOnly(survey.end);

    return staffMember.contracts.some(c => {
      const contractBegin = toDateOnly(c.begin);
      const contractEnd = toDateOnly(c.end);
      // Overlap if contractBegin <= surveyEnd && contractEnd >= surveyBegin
      return contractBegin <= surveyEnd && contractEnd >= surveyBegin;
    });
  }
}
